import Papa from "papaparse";
import React, { useState, useEffect } from "react";
import "./bulk-room-create.scss";

import ContactApi from "../../../../../../api/contact-api";
import ThreadApi from "../../../../../../api/thread-api";
import ContactHelper from "../../../../../../entities/contact/contact-helper";
import { EventEmitter } from "../../../../../../shared/utils/event-emitter";
import Events from "../../../../../../const/events";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import 'react-phone-number-input/style.css';

// Custom input component that prevents focus errors
const CustomInput = (props) => {
  // Return a hidden input that can receive focus but isn't visible
  return <input {...props} style={{ display: 'none' }} />;
};

const BulkRoomCreateModal = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [processedData, setProcessedData] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [popupVisible, setPopupVisible] = useState(false);
  const [manualContact1Name, setManualContact1Name] = useState("");
  const [manualContact1Mobile, setManualContact1Mobile] = useState("");
  const [manualContact1Country, setManualContact1Country] = useState("GB");
  const [manualContact2Name, setManualContact2Name] = useState("");
  const [manualContact2Mobile, setManualContact2Mobile] = useState("");
  const [manualContact2Country, setManualContact2Country] = useState("GB");
  const [manualProcessing, setManualProcessing] = useState(false);
  const [manualResult, setManualResult] = useState(null);

  const validateMobileNumber = (mobile) => {
    let value = mobile?.trim() || "";

    // Remove all special characters except "+"
    value = value.replace(/[^+\d]/g, "");

    if (value === "") {
      return { isValid: false, error: "Phone number is required." };
    }

    // Ensure it starts with "+"
    if (!value.startsWith("+")) {
      return {
        isValid: false,
        error:
          "Phone number must start with a country code (e.g., +1 for USA).",
      };
    }

    // Extract numeric part (excluding "+")
    const numericPart = value.replace("+", "");

    // Check minimum length
    if (numericPart.length < 10) {
      return {
        isValid: false,
        error:
          "Phone number must have at least 10 digits (excluding country code).",
      };
    }

    // Check maximum length (adjust as needed)
    if (numericPart.length > 15) {
      return {
        isValid: false,
        error: "Phone number must not exceed 15 digits.",
      };
    }

    return { isValid: true, formattedNumber: value };
  };

  const handleManualRoomCreation = async () => {
    if (
      !manualContact1Name ||
      !manualContact1Mobile ||
      !manualContact2Name ||
      !manualContact2Mobile
    ) {
      setManualResult({
        success: false,
        message: "Please enter both contacts' names and mobile numbers.",
      });
      return;
    }

    const validation1 = validateMobileNumber(manualContact1Mobile);
    const validation2 = validateMobileNumber(manualContact2Mobile);

    if (!validation1.isValid || !validation2.isValid) {
      setManualResult({
        success: false,
        message: validation1.error
          ? `First contact: ${validation1.error}`
          : `Second contact: ${validation2.error}`,
      });
      return;
    }

    setManualProcessing(true);
    setManualResult(null);

    try {
      const contact1Id = await processContact(
        manualContact1Name,
        validation1.formattedNumber
      );
      const contact2Id = await processContact(
        manualContact2Name,
        validation2.formattedNumber
      );

      const roomResult = await createRoom(
        contact1Id,
        contact2Id,
        manualContact1Name,
        manualContact2Name
      );

      setManualResult({
        success: roomResult.success,
        message: roomResult.success
          ? "Room Created Successfully"
          : roomResult.message,
      });

      if (roomResult.success) {
        setManualContact1Name("");
        setManualContact1Mobile("");
        setManualContact2Name("");
        setManualContact2Mobile("");
      }
    } catch (error) {
      setManualResult({
        success: false,
        message: error.message || "Error: Could not create room.",
      });
    } finally {
      setManualProcessing(false);
    }
  };

  const processContact = async (name, phone) => {
    try {
      // First check if contact exists
      const { data: checkResult } =
        await ContactApi.private.checkIfContactExists({ phone });

      // If contact exists, return the contact ID directly
      if (checkResult?.data?._id) {
        return checkResult.data._id;
      }

      // If no valid contact ID is found, throw an error
      throw new Error("Contact check returned an invalid response.");
    } catch (error) {
      // If the error indicates that the contact doesn't exist (404), create a new one
      if (error?.response?.status === 404) {
        try {
          const { contactId } = await ContactHelper.createNewContact({
            name,
            phone,
          });

          if (contactId) {
            return contactId;
          }

          throw new Error("Failed to create new contact.");
        } catch (createError) {
          throw new Error(`Contact creation failed: ${createError.message}`);
        }
      }

      // If it's a different error, rethrow it
      throw new Error(`Contact processing failed: ${error.message}`);
    }
  };

  const getFirstName = (fullName) => {
    return fullName.split(" ")[0];
  };

  const createRoom = async (
    contact1Id,
    contact2Id,
    contact1Name,
    contact2Name
  ) => {
    try {
      const roomTitle = `${getFirstName(contact1Name)}+${getFirstName(
        contact2Name
      )}`;

      const response = await ThreadApi.private.createThread({
        type: "room",
        title: roomTitle,
        _users: [],
        _contacts: [contact1Id, contact2Id],
      });

      return { success: true, data: response?.data };
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Unknown error occurred";

      return { success: false, message: errorMessage };
    }
  };

  const processCSV = async () => {
    if (!selectedFile) return;
    setIsProcessing(true);
    setProcessedData([]); // Clear previous results

    try {
      const csvData = await new Promise((resolve, reject) => {
        Papa.parse(selectedFile, {
          complete: (results) => {
            const filteredData = results.data.filter((row) =>
              Object.values(row).some((value) => value.trim() !== "")
            );
            resolve(filteredData);
          },
          error: (error) => reject(error),
          header: true,
          skipEmptyLines: true,
        });
      });

      const results = [];
      for (const row of csvData) {
        try {
          const validation1 = validateMobileNumber(row["Contact1 Mobile"]);
          const validation2 = validateMobileNumber(row["Contact2 Mobile"]);

          if (!validation1.isValid || !validation2.isValid) {
            results.push({
              contact1: {
                name: row["Contact1 Name"],
                mobile: row["Contact1 Mobile"],
              },
              contact2: {
                name: row["Contact2 Name"],
                mobile: row["Contact2 Mobile"],
              },
              status: {
                success: false,
                message: validation1.isValid
                  ? `Second contact: ${validation2.error}`
                  : `First contact: ${validation1.error}`,
              },
            });
            setProcessedData([...results]);
            continue;
          }

          const contact1Id = await processContact(
            row["Contact1 Name"],
            validation1.formattedNumber
          );
          const contact2Id = await processContact(
            row["Contact2 Name"],
            validation2.formattedNumber
          );

          const roomResult = await createRoom(
            contact1Id,
            contact2Id,
            row["Contact1 Name"],
            row["Contact2 Name"]
          );

          results.push({
            contact1: {
              name: row["Contact1 Name"],
              mobile: validation1.formattedNumber,
            },
            contact2: {
              name: row["Contact2 Name"],
              mobile: validation2.formattedNumber,
            },
            status: {
              success: roomResult.success,
              message: roomResult.success ? "CREATED" : roomResult.message,
            },
          });

          setProcessedData([...results]);
        } catch (error) {
          results.push({
            contact1: {
              name: row["Contact1 Name"],
              mobile: row["Contact1 Mobile"],
            },
            contact2: {
              name: row["Contact2 Name"],
              mobile: row["Contact2 Mobile"],
            },
            status: { success: false, message: error.message || "INVALID" },
          });
          setProcessedData([...results]);
        }
      }

      setShowResults(true);
    } catch (error) {
      console.error("Error processing CSV:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDownloadSample = () => {
    const sampleData =
      "Contact1 Name,Contact1 Mobile,Contact2 Name,Contact2 Mobile\nJohn Doe,+447123456789,Jane Doe,+447987654321";
    const blob = new Blob([sampleData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_contacts.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const _registerEvents = () => {
      EventEmitter.subscribe(Events.SHOW_CSV_ROOM_IMPORT_POPUP, () => {
        setPopupVisible(true);
      });
      EventEmitter.subscribe(Events.HIDE_CSV_ROOM_IMPORT_POPUP, () => {
        setPopupVisible(false);
      });
    };
    const _unregisterEvents = () => {
      EventEmitter.cancelAll(Events.SHOW_CSV_ROOM_IMPORT_POPUP);
      EventEmitter.cancelAll(Events.HIDE_CSV_ROOM_IMPORT_POPUP);
    };
    _registerEvents();
    return () => {
      _unregisterEvents();
    };
  }, []);

  if (!popupVisible) {
    return null;
  }

  return (
    <div className="modal-wrapper">
      <div
        className="modal fade show"
        id="newCSVFileUploadModal"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="newCSVFileUploadModalTitle"
        aria-hidden={false}
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="newCSVFileUploadModalTitle">
                Connect Contacts
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => {
                  setSelectedFile(null);
                  setShowResults(false);
                  setProcessedData([]);
                  setPopupVisible(false);
                }}
              ></button>
            </div>

            <div className="modal-body">
              {!showResults ? (
                <div className="contact-creation-container">
                  <div className="manual-creation-section">
                    <h6 className="section-title">Create a new chat</h6>
                    
                    <div className="contact-form-row">
                      <div className="contact-form-col">
                        <label htmlFor="contact1Name">First contact</label>
                        <input
                          type="text"
                          id="contact1Name"
                          className="form-control"
                          placeholder="Enter contact name"
                          value={manualContact1Name}
                          onChange={(e) => setManualContact1Name(e.target.value)}
                        />
                        <div className="phone-input-container">
                          <PhoneInputWithCountrySelect
                            defaultCountry="GB"
                            value={manualContact1Mobile}
                            onChange={setManualContact1Mobile}
                            onCountryChange={setManualContact1Country}
                            international={true}
                            withCountryCallingCode={true}
                            countryCallingCodeEditable={false}
                            countrySelectProps={{ unicodeFlags: true }}
                          />
                        </div>
                      </div>

                      <div className="contact-form-col">
                        <label htmlFor="contact2Name">Second contact</label>
                        <input
                          type="text"
                          id="contact2Name"
                          className="form-control"
                          placeholder="Enter contact name"
                          value={manualContact2Name}
                          onChange={(e) => setManualContact2Name(e.target.value)}
                        />
                        <div className="phone-input-container">
                          <PhoneInputWithCountrySelect
                            defaultCountry="GB"
                            value={manualContact2Mobile}
                            onChange={setManualContact2Mobile}
                            onCountryChange={setManualContact2Country}
                            international={true}
                            withCountryCallingCode={true}
                            countryCallingCodeEditable={false}
                            countrySelectProps={{ unicodeFlags: true }}
                          />
                        </div>
                      </div>
                    </div>

                    {manualResult && (
                      <div className={`alert ${
                        manualResult.success 
                          ? "alert-success" 
                          : manualResult.message.includes("already in a room")
                            ? "alert-info"
                            : "alert-danger"
                      }`}>
                        {manualResult.success ? (
                          <><i className="fas fa-check-circle"></i> {manualResult.message}</>
                        ) : manualResult.message.includes("already in a room") ? (
                          <><i className="fas fa-info-circle"></i> {manualResult.message}</>
                        ) : (
                          <><i className="fas fa-exclamation-triangle"></i> {manualResult.message}</>
                        )}
                      </div>
                    )}

                    <div className="action-button-container">
                      <button
                        className="bouncy-button small"
                        onClick={handleManualRoomCreation}
                        disabled={manualProcessing}
                      >
                        {manualProcessing ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            <span>Processing...</span>
                          </>
                        ) : (
                          <span>Start a new chat</span>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="divider">
                    <span className="divider-text">OR</span>
                  </div>

                  <div className="bulk-import-section">
                    <h6 className="section-title">Bulk import contacts</h6>
                    
                    <div className="csv-upload-container">
                      <input
                        type="file"
                        id="csvFileInput"
                        className="csv-file-input"
                        accept=".csv"
                        onChange={handleFileUpload}
                      />
                      <label htmlFor="csvFileInput" className="csv-upload-label">
                        <i className="fas fa-cloud-upload-alt"></i>
                        <span>{selectedFile?.name || "Upload CSV file"}</span>
                      </label>
                    </div>
                    
                    <div className="sample-download-container">
                      <button
                        className="bouncy-button small"
                        onClick={handleDownloadSample}
                      >
                        <i className="fas fa-download me-2"></i>
                        <span>Download Sample CSV</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="results-container">
                  <h6 className="section-title mb-3">Processing Results</h6>
                  <div className="table-responsive results-table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Contact 1</th>
                          <th>Contact 2</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {processedData.map((row, index) => (
                          <tr key={index}>
                            <td>
                              <div className="contact-info">
                                <div className="contact-name">{row.contact1.name}</div>
                                <div className="contact-phone">{row.contact1.mobile}</div>
                              </div>
                            </td>
                            <td>
                              <div className="contact-info">
                                <div className="contact-name">{row.contact2.name}</div>
                                <div className="contact-phone">{row.contact2.mobile}</div>
                              </div>
                            </td>
                            <td>
                              {row.status?.success ? (
                                <span className="status-badge status-success">
                                  <i className="fas fa-check-circle me-1"></i> Created
                                </span>
                              ) : row.status?.message.includes("already in a room") ? (
                                <span className="status-badge status-existing">
                                  <i className="fas fa-info-circle me-1"></i> Already in a room
                                </span>
                              ) : (
                                <span className="status-badge status-error">
                                  <i className="fas fa-exclamation-triangle me-1"></i> {row.status.message}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {!showResults ? (
                <>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setSelectedFile(null);
                      setPopupVisible(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="bouncy-button small"
                    disabled={!selectedFile || isProcessing}
                    onClick={processCSV}
                  >
                    {isProcessing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <span>Import Contacts</span>
                    )}
                  </button>
                </>
              ) : (
                <div className="d-flex w-100 justify-content-between">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setShowResults(false);
                      setPopupVisible(false);
                    }}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setShowResults(false);
                      setSelectedFile(null);
                      setProcessedData([]);
                    }}
                  >
                    Import More Contacts
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkRoomCreateModal;
